import { Dialog } from "@base-ui/react/dialog";
import clsx from "clsx";
import React, { forwardRef, useContext } from "react";
import { IconClose } from "../icons";
import {
  modalBodyClass,
  modalContentClass,
  modalFooterClass,
  modalHeaderClass,
  modalOverlayClass,
  modalTitlesClass,
} from "./styles.css";

const ModalContext = React.createContext<{ toggle?: () => void } | undefined>(
  undefined,
);

type ModalProps = Omit<React.HTMLAttributes<HTMLDivElement>, "open"> & {
  open?: boolean;
  toggle?: () => void;
  overlayStyles?: React.CSSProperties;
};

const Modal = forwardRef(function Modal(
  { open, toggle, overlayStyles, children, className, ...props }: ModalProps,
  ref: React.Ref<HTMLDivElement>,
) {
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) toggle?.();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop
          className={modalOverlayClass}
          style={overlayStyles}
          data-slot="modal-overlay"
        >
          <Dialog.Popup
            className={clsx(modalContentClass, className)}
            ref={ref}
            data-slot="modal-content"
            {...props}
          >
            <ModalContext.Provider value={{ toggle }}>
              {children}
            </ModalContext.Provider>
          </Dialog.Popup>
        </Dialog.Backdrop>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

interface ModalHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  hasClose?: boolean;
}

const Header = ({
  children,
  hasClose,
  className,
  ...props
}: ModalHeaderProps) => {
  const context = useContext(ModalContext);
  const toggle = context?.toggle;

  return (
    <div
      className={clsx(modalHeaderClass, className)}
      data-slot="modal-header"
      {...props}
    >
      <div className={modalTitlesClass} data-slot="modal-titles">
        {children}
      </div>
      {hasClose && (
        <button
          type="button"
          onClick={() => toggle?.()}
          aria-label="Close"
          className="modal-close-button"
          data-slot="modal-close"
        >
          <IconClose />
        </button>
      )}
    </div>
  );
};

interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {}

const Body = ({ children, className, ...props }: ModalBodyProps) => {
  return (
    <div
      className={clsx(modalBodyClass, className)}
      data-slot="modal-body"
      {...props}
    >
      {children}
    </div>
  );
};

interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const Footer = ({ children, className, ...props }: ModalFooterProps) => {
  return (
    <div
      className={clsx(modalFooterClass, className)}
      data-slot="modal-footer"
      {...props}
    >
      {children}
    </div>
  );
};

const ModalComponent = Object.assign(Modal, { Header, Body, Footer });

export default ModalComponent;

Modal.displayName = "Modal";
